import { KNOWN_EVENT_CATEGORIES, MITRE_TACTICS, SEVERITY_ORDER } from '@bartizo/shared';
import { useId, type ReactNode } from 'react';
import {
  anyEventFilter,
  EVENT_RANGES,
  NO_EVENT_FILTERS,
  type EventFilterState,
} from '../lib/event-filters.js';
import { Button } from '../ui/Button.js';
import { SelectField, TextField } from '../ui/Field.js';

/**
 * The filters of one server's events (its Events tab): severity, category, state,
 * time and ATT&CK, the same ones the Events page has, less the server and group
 * the tab is already of.
 */

export function ServerEventFilters({
  value,
  onChange,
}: {
  value: EventFilterState;
  onChange: (next: EventFilterState) => void;
}): ReactNode {
  const categoryListId = useId();
  const tacticListId = useId();
  const set = (patch: Partial<EventFilterState>): void => {
    onChange({ ...value, ...patch });
  };
  return (
    <div className="filters">
      <SelectField
        label="Severity"
        hint="This level and everything worse."
        value={value.severity === '' ? 'any' : value.severity}
        onChange={(event) => {
          set({ severity: event.target.value === 'any' ? '' : event.target.value });
        }}
      >
        <option value="any">Any severity</option>
        {[...SEVERITY_ORDER].reverse().map((option) => (
          <option key={option} value={option}>
            {option} and above
          </option>
        ))}
      </SelectField>

      <TextField
        label="Category"
        type="search"
        list={categoryListId}
        placeholder="fim"
        value={value.category}
        onChange={(event) => {
          set({ category: event.target.value });
        }}
      />
      <datalist id={categoryListId}>
        {KNOWN_EVENT_CATEGORIES.map((category) => (
          <option key={category} value={category} />
        ))}
      </datalist>

      <TextField
        label="MITRE ATT&CK"
        hint="A tactic, or a technique such as T1070.004."
        type="search"
        list={tacticListId}
        placeholder="Defense Evasion"
        value={value.attack}
        onChange={(event) => {
          set({ attack: event.target.value });
        }}
      />
      <datalist id={tacticListId}>
        {MITRE_TACTICS.map((tactic) => (
          <option key={tactic} value={tactic} />
        ))}
      </datalist>

      <SelectField
        label="State"
        value={value.state === '' ? 'any' : value.state}
        onChange={(event) => {
          set({ state: event.target.value === 'any' ? '' : event.target.value });
        }}
      >
        <option value="any">Any state</option>
        <option value="open">Not acknowledged</option>
        <option value="done">Acknowledged</option>
      </SelectField>

      <SelectField
        label="Time"
        value={value.hours}
        onChange={(event) => {
          set({ hours: event.target.value });
        }}
      >
        {EVENT_RANGES.map((range) => (
          <option key={range.value} value={range.value}>
            {range.label}
          </option>
        ))}
      </SelectField>

      {anyEventFilter(value) && (
        <div className="field">
          <span className="visually-hidden">Filters</span>
          <Button
            variant="ghost"
            onClick={() => {
              onChange(NO_EVENT_FILTERS);
            }}
          >
            Clear filters
          </Button>
        </div>
      )}
    </div>
  );
}
